import { useState } from 'react';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { useMutation } from 'react-query';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@tigergraph/app-ui-lib/modal';
import { Input } from '@tigergraph/app-ui-lib/input';
import { FormatValidator } from '@tigergraph/tools-models/utils';
import { AxiosError } from 'axios';
import ErrorMessage from '../ErrorMessage';
import ConfirmButtons from '@/components/ConfirmButtons';
import { expand } from 'inline-style-expand-shorthand';
import { showToast } from '@/components/styledToasterContainer';
import { KIND } from 'baseui/toast';
import { ID_TOKEN_KEY, useWorkspaceContext } from '@/contexts/workspaceContext';
import { createGraph } from '@tigergraph/tools-models';
import { useOrgContext } from '@/contexts/orgContext';
import { trackGraphCreatedEvent } from '@/utils/analytics/trackEngineEvents';
import { GraphCreationMethod, GraphCreationSurface, OnboardingPath } from '@/utils/analytics/types';
import { JourneyName } from '@/utils/analytics/taxonomy';
import { ingestionFlowIdAtom } from '@/pages/ingestion/atom';
import { useAtom } from 'jotai';

export interface CreateNewGraphProps {
  isOpen: boolean;
  message?: string;
  onClose: (graphName: string) => void;
  // Analytics only: the product surface this modal was opened from. The same modal is reached
  // from Design Schema, the Query Editor, the Load Data wizard and Build AI's name-conflict
  // recovery, and EVT-006 reports them apart.
  creationSurface?: GraphCreationSurface;
  creationMethod?: GraphCreationMethod;
  onboardingPath?: OnboardingPath;
  journeyName?: JourneyName;
}

export default function CreateNewGraphModal(props: CreateNewGraphProps) {
  const [css] = useStyletron();
  const {
    isOpen,
    message,
    onClose,
    creationSurface = 'design_schema',
    creationMethod = 'manual',
    onboardingPath,
    journeyName,
  } = props;
  const [ingestionFlowId] = useAtom(ingestionFlowIdAtom);
  const { setCurrentGraph, currentWorkspace, refetchSimpleAuth } = useWorkspaceContext();
  const { userInfo, currentOrg } = useOrgContext();
  const [graphName, setGraphName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  const { mutate: mutateCreateGraph, isLoading } = useMutation(
    ['createGraph'],
    async (graphName: string) => {
      const response = await createGraph(
        {
          GraphName: graphName,
          VertexTypes: [],
          EdgeTypes: [],
        },
        {
          baseURL: `https://${currentWorkspace?.nginx_host}`,
          version: currentWorkspace?.tg_version,
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem(ID_TOKEN_KEY)}`,
          },
        }
      );
      return response;
    },
    {
      onSuccess: () => {
        // A graph created here is always empty and hand-made: the modal posts a schema
        // with no vertex or edge types.
        trackGraphCreatedEvent(
          {
            graph_source: 'customer_created',
            graph_creation_method: creationMethod,
            graph_creation_surface: creationSurface,
            initial_content_state: 'empty',
            created_by_actor_type: 'user',
            vertex_type_count: 0,
            edge_type_count: 0,
            // The attempt id is only meaningful when this modal really is part of the
            // ingestion flow; it outlives the drawer, so any other surface would inherit a
            // stale one.
            ingestion_flow_id: creationSurface === 'load_data' ? ingestionFlowId : undefined,
            onboarding_path: onboardingPath,
            journey_name: journeyName,
          },
          { userId: userInfo?.id, organizationId: currentOrg?.org_id, workspace: currentWorkspace, graphId: graphName }
        );
        handleClose();
        refetchSimpleAuth();
        setCurrentGraph(graphName);
        showToast({
          kind: KIND.positive,
          message: `Successfully created graph ${graphName}.`,
        });
      },
      onError: (error: AxiosError<{ message: string }>) => {
        setErrorMessage(error.response?.data?.message);
        showToast({
          kind: KIND.negative,
          message: `Failed to create graph ${graphName}\n${error.response?.data?.message}`,
        });
      },
    }
  );

  const handleClose = () => {
    onClose(graphName);
    setGraphName('');
    setErrorMessage(undefined);
  };

  return (
    <div
      className={css({
        display: 'flex',
        width: '100%',
      })}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !errorMessage) {
          mutateCreateGraph(graphName);
        }
      }}
    >
      <Modal
        onClose={handleClose}
        isOpen={isOpen}
        overrides={{
          Root: {
            style: {
              zIndex: 2,
            },
          },
        }}
      >
        <ModalHeader
          className={css({
            fontFamily: 'Urbanist',
            fontSize: '16px',
            fontWeight: 600,
            ...expand({
              padding: '0',
            }),
          })}
        >
          Create New Graph
        </ModalHeader>
        <ModalBody>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              rowGap: '8px',
            })}
          >
            {message && (
              <div
                className={css({
                  fontSize: '14px',
                  fontWeight: 400,
                })}
              >
                {message}
              </div>
            )}
            <div
              className={css({
                fontSize: '14px',
                fontWeight: 400,
              })}
            >
              Graph Name
            </div>
            <Input
              aria-label="graph-name-input"
              value={graphName}
              error={!!errorMessage}
              disabled={isLoading}
              onChange={(event) => {
                setGraphName(event.currentTarget.value);
                const formatCheck = FormatValidator.isName(event.currentTarget.value);
                if (!formatCheck.success) {
                  setErrorMessage(formatCheck.message);
                } else {
                  setErrorMessage(undefined);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !errorMessage) {
                  mutateCreateGraph(graphName);
                }
                e.stopPropagation();
              }}
              overrides={{
                Root: {
                  style: {
                    ...expand({
                      borderRadius: '2px',
                    }),
                  },
                },
              }}
            />
            {errorMessage && <ErrorMessage message={errorMessage} />}
          </div>
        </ModalBody>
        <ModalFooter>
          <ConfirmButtons
            confirmLabel={'Done'}
            cancelLabel={'Cancel'}
            confirmLoading={isLoading}
            cancelDisabled={isLoading}
            confirmDisabled={!!errorMessage}
            onConfirm={() => {
              mutateCreateGraph(graphName);
            }}
            onCancel={handleClose}
          />
        </ModalFooter>
      </Modal>
    </div>
  );
}
